import React, { type FC, type PropsWithChildren, type ReactNode } from 'react'
import { Link as BaseLink, Box, type LinkProps, type SxProps, type Theme, Typography } from '@mui/material'

import type { Translation } from '../../../domain'

import {
    type BaseCommonProps,
    LengthLimitedField as BaseLengthLimitedField,
    type LengthLimitedFieldProps as BaseLengthLimitedFieldProps,
    TranslatedTextField,
    type TranslatedTextFieldProps,
    Validation,
    validationClasses,
} from '../common'
import { FormattedTranslation } from '../Internationalization'

const validationsStyle: SxProps<Theme> = {
    [`& .${validationClasses.icon}`]: { my: 0, color: 'white' },
    [`& .${validationClasses.root}`]: { mb: 4 },
    [`& .${validationClasses.label}`]: { color: 'grey.200', textAlign: 'start' },
}

const baseTextFieldProps = {
    slotProps: {
        inputLabel: {
            sx: { color: 'white !important' },
        },
    },
} as const

export const LengthLimitedField: FC<Pick<BaseLengthLimitedFieldProps, 'type' | 'value' | 'disabled' | 'onChange' | 'autoFocus'>> = (props) => (
    <BaseLengthLimitedField {...baseTextFieldProps} {...props} />
)

export const Field: FC<
    Pick<TranslatedTextFieldProps, 'name' | 'id' | 'type' | 'disabled' | 'autoFocus' | 'label' | 'value' | 'onKeyUp' | 'onChange' | 'autoComplete'>
> = (props) => <TranslatedTextField {...baseTextFieldProps} fullWidth required {...props} />

const linkStyle: SxProps<Theme> = { cursor: 'pointer' }

export const Link: FC<PropsWithChildren<{ disabled?: boolean, onClick?: VoidFunction } & Pick<LinkProps, 'color'>>> = ({
    onClick,
    disabled,
    color = 'secondary',
    ...props
}) => <BaseLink display="block" underline="none" fontSize="small" sx={linkStyle} color={color} onClick={disabled ? undefined : onClick} {...props} />

const formChildStyle: SxProps<Theme> = { mt: 4 }

export const Form: FC<Pick<BaseCommonProps, 'id'> & Readonly<{ title: Translation, children: readonly ReactNode[] }>> = ({ title, children, ...props }) => (
    <Box {...props}>
        <Typography variant="h6" color="white">
            <FormattedTranslation id={title} />
        </Typography>
        {children.map((child, k) => (
            <Box key={k} sx={formChildStyle}>
                {child}
            </Box>
        ))}
    </Box>
)

const loginValidationErrorStyle: SxProps<Theme> = { background: '#ED6C0280', borderRadius: 1, px: 3, py: 2, mb: 6 }

export const LoginValidationError: FC<PropsWithChildren<Pick<BaseCommonProps, 'id' | 'sx'>>> = ({ children, sx, ...props }) => (
    <Validation {...props} error sx={{ ...validationsStyle, ...loginValidationErrorStyle, ...sx }}>
        {children}
    </Validation>
)
